import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  AlertTriangle,
  GitMerge,
  LoaderCircle,
  UserPlus,
  X,
} from "lucide-react";
import { db } from "../../firebase";
import { showErrorToast, showSuccessToast } from "../../toastmsg";
import { convertToDisplayDate } from "../../utils/date";
import ConfirmationDialog from "../ConfirmationDialogs/ConfirmationDialogs";

const PENDING_STATUSES = [
  "pending-review",
  "protected-match",
  "ambiguous-match",
];

type Candidate = {
  leadId: string;
  matchedFields: string[];
  authenticatedOwner: boolean;
  name?: string;
  email?: string;
  phoneNumber?: string;
  ic?: string;
};

type PendingSubmission = {
  id: string;
  status: string;
  submissionType?: string;
  submittedAt?: unknown;
  submittedData?: Record<string, any>;
  matchResult?: {
    candidateLeadIds?: string[];
    candidates?: Candidate[];
  };
};

type ReviewGroup = {
  key: string;
  leadId?: string;
  submissions: PendingSubmission[];
};

type ResolutionAction = "merge" | "create-new" | "reject";

const sourceLabel = (type?: string) =>
  ({
    "manual-add": "Add Lead",
    "bulk-upload": "Upload Leads",
    "uec2026-registration": "UEC2026 Landing Page",
  })[type || ""] ||
  type ||
  "Unknown Source";

const statusLabel = (status: string) =>
  ({
    "pending-review": "Possible duplicate",
    "protected-match": "Authenticated account",
    "ambiguous-match": "Multiple matches",
  })[status] || status;

const statusBadgeClass = (status: string) => {
  if (status === "protected-match") {
    return "bg-red-100 text-red-800 ring-1 ring-red-200";
  }
  if (status === "ambiguous-match") {
    return "bg-purple-100 text-purple-800 ring-1 ring-purple-200";
  }
  return "bg-amber-100 text-amber-800 ring-1 ring-amber-200";
};

const queueCardClass = (status: string, selected: boolean) => {
  if (selected) return "border-black bg-blue-50";
  return "border-gray-400 bg-white hover:border-black";
  // if (status === "protected-match") {
  //   return "border-red-300 bg-red-50 hover:border-red-500";
  // }
  // if (status === "ambiguous-match") {
  //   return "border-purple-300 bg-purple-50 hover:border-purple-500";
  // }
};

const displayValue = (value: unknown) => String(value || "Not provided");

export default function PendingLeadReviews({
  onResolved,
}: {
  onResolved?: () => void | Promise<void>;
}) {
  const [submissions, setSubmissions] = useState<PendingSubmission[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedGroupKey, setSelectedGroupKey] = useState("");
  const [selected, setSelected] = useState<PendingSubmission | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [candidateLeadId, setCandidateLeadId] = useState("");
  const [reason, setReason] = useState("");
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [pendingAction, setPendingAction] = useState<ResolutionAction | null>(
    null,
  );

  const reviewGroups = useMemo(() => {
    const groups = new Map<string, ReviewGroup>();

    submissions.forEach((submission) => {
      const candidateIds = submission.matchResult?.candidateLeadIds || [];
      const isSingleLeadMatch = candidateIds.length === 1;
      const key = isSingleLeadMatch
        ? `lead:${candidateIds[0]}`
        : `ambiguous:${submission.id}`;
      const current = groups.get(key);

      if (current) {
        current.submissions.push(submission);
      } else {
        groups.set(key, {
          key,
          leadId: isSingleLeadMatch ? candidateIds[0] : undefined,
          submissions: [submission],
        });
      }
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        submissions: [...group.submissions].sort(
          (left, right) =>
            new Date(String(right.submittedAt || "")).getTime() -
            new Date(String(left.submittedAt || "")).getTime(),
        ),
      }))
      .sort(
        (left, right) =>
          new Date(String(right.submissions[0]?.submittedAt || "")).getTime() -
          new Date(String(left.submissions[0]?.submittedAt || "")).getTime(),
      );
  }, [submissions]);

  const selectedGroup = useMemo(
    () => reviewGroups.find((group) => group.key === selectedGroupKey) || null,
    [reviewGroups, selectedGroupKey],
  );

  useEffect(() => {
    const pendingQuery = query(
      collection(db, "lead-submissions"),
      where("status", "in", PENDING_STATUSES),
    );
    return onSnapshot(
      pendingQuery,
      (snapshot) => {
        const next = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }) as PendingSubmission)
          .sort(
            (left, right) =>
              new Date(String(right.submittedAt || "")).getTime() -
              new Date(String(left.submittedAt || "")).getTime(),
          );
        setSubmissions(next);
      },
      (error) => {
        console.error("Failed to load pending lead reviews:", error);
        showErrorToast("Unable to load pending lead reviews.");
      },
    );
  }, []);

  useEffect(() => {
    if (!selectedGroupKey) return;
    const group = reviewGroups.find((item) => item.key === selectedGroupKey);
    if (!group) {
      setSelectedGroupKey("");
      setSelected(null);
      return;
    }
    setSelected(
      (current) =>
        group.submissions.find((item) => item.id === current?.id) ||
        group.submissions[0] ||
        null,
    );
  }, [reviewGroups, selectedGroupKey]);

  useEffect(() => {
    if (!selected) {
      setCandidates([]);
      setCandidateLeadId("");
      return;
    }
    const candidateMetadata = selected.matchResult?.candidates || [];
    const candidateIds = selected.matchResult?.candidateLeadIds || [];
    setLoadingCandidates(true);
    Promise.all(
      candidateIds.map(async (leadId) => {
        const snapshot = await getDoc(doc(db, "leads", leadId));
        const metadata = candidateMetadata.find(
          (item) => item.leadId === leadId,
        );
        return {
          leadId,
          matchedFields: metadata?.matchedFields || [],
          authenticatedOwner:
            metadata?.authenticatedOwner ||
            Boolean(snapshot.data()?.studentUID),
          ...(snapshot.exists() ? snapshot.data() : {}),
        } as Candidate;
      }),
    )
      .then((items) => {
        setCandidates(items);
        setCandidateLeadId(items.length === 1 ? items[0].leadId : "");
      })
      .catch((error) => {
        console.error("Failed to load matched leads:", error);
        showErrorToast("Unable to load matched leads.");
      })
      .finally(() => setLoadingCandidates(false));
  }, [selected]);

  const incoming = selected?.submittedData || {};
  const selectedCandidate = useMemo(
    () => candidates.find((item) => item.leadId === candidateLeadId),
    [candidateLeadId, candidates],
  );

  const requestResolution = (action: ResolutionAction) => {
    if (!selected || (action === "merge" && !candidateLeadId)) {
      showErrorToast("Select a matched lead before merging.");
      return;
    }
    if (action !== "merge" && !reason.trim()) {
      showErrorToast(
        "Enter a reason before creating a separate lead or rejecting.",
      );
      return;
    }
    setPendingAction(action);
  };

  const resolve = async (action: ResolutionAction) => {
    setPendingAction(null);
    if (!selected || (action === "merge" && !candidateLeadId)) {
      showErrorToast(
        "The selected submission or matched lead is no longer available.",
      );
      return;
    }
    if (action !== "merge" && !reason.trim()) {
      showErrorToast(
        "Enter a reason before creating a separate lead or rejecting.",
      );
      return;
    }
    setResolving(true);
    try {
      const functions = getFunctions(undefined, "asia-southeast1");
      const resolveSubmission = httpsCallable(
        functions,
        "resolveStaffLeadSubmission",
      );
      await resolveSubmission({
        submissionId: selected.id,
        action,
        candidateLeadId: action === "merge" ? candidateLeadId : undefined,
        reason: reason.trim(),
      });
      showSuccessToast(
        action === "merge"
          ? "Submission merged into the selected lead."
          : action === "create-new"
            ? "Submission created as a separate lead."
            : "Submission rejected.",
      );
      setReason("");
      await onResolved?.();
    } catch (error: any) {
      console.error("Failed to resolve lead submission:", error);
      showErrorToast(error?.message || "Failed to resolve submission.");
    } finally {
      setResolving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 font-medium text-amber-800 hover:bg-amber-100 cursor-pointer"
      >
        <AlertTriangle className="h-4 w-4" />
        Pending Review
        <span className="rounded-full bg-amber-600 px-2 py-0.5 text-xs text-white">
          {submissions.length}
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="flex h-full w-full max-w-5xl flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-2">
              <div>
                <h2 className="text-lg font-bold">Pending Lead Review</h2>
                <p className="text-sm text-gray-500">
                  Review possible duplicates before changing canonical leads.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[320px_1fr]">
              <div className="overflow-y-auto border-r bg-gray-50 p-2">
                {submissions.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500">
                    No pending reviews.
                  </p>
                ) : (
                  reviewGroups.map((group) => {
                    const latest = group.submissions[0];
                    const isAmbiguous = !group.leadId;
                    return (
                      <button
                        type="button"
                        key={group.key}
                        onClick={() => {
                          setSelectedGroupKey(group.key);
                          setSelected(latest);
                          setReason("");
                        }}
                        className={`mb-1 w-full rounded-md border border-gray-400 p-1 text-left cursor-pointer ${queueCardClass(
                          latest.status,
                          selectedGroupKey === group.key,
                        )}`}
                      >
                        <div className="flex justify-between items-center">
                          <p className="text-sm font-semibold">
                            {displayValue(latest.submittedData?.name)}
                          </p>
                          <p className="mt-1 text-xs text-gray-800">
                            {convertToDisplayDate(latest.submittedAt) ||
                              "Date unavailable"}
                          </p>
                        </div>
                        <p className="text-xs text-gray-600">
                          {isAmbiguous
                            ? "Multiple possible leads"
                            : `${group.submissions.length} pending submission${
                                group.submissions.length === 1 ? "" : "s"
                              }`}
                        </p>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="overflow-y-auto p-5">
                {!selectedGroup || !selected ? (
                  <div className="flex h-full items-center justify-center text-gray-500">
                    Select a submission to review.
                  </div>
                ) : (
                  <div className="space-y-3">
                    <section>
                      <h3 className="mb-2 font-bold">
                        Review-required submissions (
                        {selectedGroup.submissions.length})
                      </h3>
                      <div className="space-y-2">
                        {selectedGroup.submissions.map((submission, index) => (
                          <button
                            type="button"
                            key={submission.id}
                            onClick={() => {
                              setSelected(submission);
                              setReason("");
                            }}
                            className={`flex w-full items-center justify-between rounded-md border p-2 text-left ${
                              selected.id === submission.id
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-gray-400"
                            }`}
                          >
                            <div>
                              <p className="text-sm font-semibold">
                                {displayValue(submission.submittedData?.name)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {sourceLabel(submission.submissionType)}
                              </p>
                            </div>
                            <div className="text-right">
                              {index === 0 && (
                                <p className="text-xs font-medium text-blue-700">
                                  Newest
                                </p>
                              )}
                              <p className="text-xs text-gray-600">
                                {convertToDisplayDate(submission.submittedAt) ||
                                  "Date unavailable"}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </section>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                        {sourceLabel(selected.submissionType)}
                      </span>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${statusBadgeClass(
                          selected.status,
                        )}`}
                      >
                        {statusLabel(selected.status)}
                      </span>
                    </div>

                    <div className="grid gap-2 lg:grid-cols-2">
                      <section className="rounded-md border border-blue-200 bg-blue-50 p-2">
                        <h3 className="mb-2 font-bold text-sm text-blue-900">
                          Incoming submission
                        </h3>
                        {[
                          ["Name", incoming.name],
                          ["Email", incoming.email],
                          ["Phone", incoming.phoneNumber],
                          ["IC", incoming.ic],
                        ].map(([label, value]) => (
                          <p key={String(label)} className="mb-1 text-xs">
                            <span className="font-medium text-gray-600">
                              {label}:{" "}
                            </span>
                            {displayValue(value)}
                          </p>
                        ))}
                      </section>

                      <section className="rounded-md border border-gray-400 p-2">
                        <h3 className="mb-2 text-sm font-bold">Matched lead</h3>
                        {loadingCandidates ? (
                          <LoaderCircle className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            {candidates.length > 1 && (
                              <select
                                value={candidateLeadId}
                                onChange={(event) =>
                                  setCandidateLeadId(event.target.value)
                                }
                                className="mb-2 w-full rounded-md border p-1 text-sm"
                              >
                                <option value="">Select matched lead</option>
                                {candidates.map((candidate) => (
                                  <option
                                    key={candidate.leadId}
                                    value={candidate.leadId}
                                  >
                                    {candidate.name ||
                                      candidate.email ||
                                      candidate.leadId}
                                  </option>
                                ))}
                              </select>
                            )}
                            {selectedCandidate ? (
                              <>
                                {selectedCandidate.authenticatedOwner && (
                                  <p className="mb-3 rounded bg-red-50 p-1 text-xs font-medium text-red-700">
                                    Authenticated owner: identity and account
                                    fields will be preserved.
                                  </p>
                                )}
                                {[
                                  ["Name", selectedCandidate.name],
                                  ["Email", selectedCandidate.email],
                                  ["Phone", selectedCandidate.phoneNumber],
                                  ["IC", selectedCandidate.ic],
                                  [
                                    "Matched by",
                                    selectedCandidate.matchedFields.join(", "),
                                  ],
                                ].map(([label, value]) => (
                                  <p
                                    key={String(label)}
                                    className="mb-1 text-xs"
                                  >
                                    <span className="font-medium text-gray-600">
                                      {label}:{" "}
                                    </span>
                                    {displayValue(value)}
                                  </p>
                                ))}
                              </>
                            ) : (
                              <p className="text-sm text-gray-500">
                                Select a matched lead.
                              </p>
                            )}
                          </>
                        )}
                      </section>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium">
                        Decision reason (required for Create New or Reject)
                      </label>
                      <textarea
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        className="w-full rounded-md border p-2"
                        rows={2}
                      />
                    </div>

                    <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
                      <button
                        type="button"
                        disabled={resolving}
                        onClick={() => requestResolution("reject")}
                        className="rounded-md border border-red-300 px-2 py-1 text-red-700 hover:bg-red-50 disabled:opacity-50 cursor-pointer"
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        disabled={resolving}
                        onClick={() => requestResolution("create-new")}
                        className="inline-flex items-center gap-2 rounded-md border border-blue-300 px-2 py-1 text-blue-700 hover:bg-blue-50 disabled:opacity-50 cursor-pointer"
                      >
                        <UserPlus className="h-4 w-4" /> Create New
                      </button>
                      <button
                        type="button"
                        disabled={resolving || !candidateLeadId}
                        onClick={() => requestResolution("merge")}
                        className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-2 py-1 text-white hover:bg-emerald-700 disabled:opacity-50 cursor-pointer"
                      >
                        {resolving ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          <GitMerge className="h-4 w-4" />
                        )}
                        Merge
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={pendingAction !== null}
        icon={
          pendingAction === "merge" ? (
            <GitMerge className="h-12 w-12 rounded-xl bg-emerald-100 p-2 text-emerald-600" />
          ) : pendingAction === "create-new" ? (
            <UserPlus className="h-12 w-12 rounded-xl bg-blue-100 p-2 text-blue-600" />
          ) : (
            <AlertTriangle className="h-12 w-12 rounded-xl bg-red-100 p-2 text-red-600" />
          )
        }
        title={
          pendingAction === "merge"
            ? "Merge Submission"
            : pendingAction === "create-new"
              ? "Create New Lead"
              : "Reject Submission"
        }
        message={
          pendingAction === "merge"
            ? "Merge this submission into the selected canonical lead?"
            : pendingAction === "create-new"
              ? "Confirm that this submission belongs to a different person and create a new lead?"
              : "Reject this submission without changing or creating a lead?"
        }
        onCancel={() => setPendingAction(null)}
        onConfirm={() => pendingAction && void resolve(pendingAction)}
      />
    </>
  );
}
